import { Router } from '@angular/router';

export class ModuleLoader {
    private router!: Router;
    constructor(
        private environment: any,
        private bundlesScriptBasePath = '',
        private loadElements = true,
    ) {
        if (!environment) {
            throw new Error('Environment not provided');
        }
    }

    public initializer = (router: Router) => () => {
        this.router = router;
        return new Promise(async (resolve, reject) => {
            const durationFromEnv = this.environment.PLUGIN_TIMEOUT_MS
                ? parseInt(this.environment.PLUGIN_TIMEOUT_MS, 10)
                : 10000;
            const duration = isNaN(durationFromEnv) ? 10000 : durationFromEnv;
            console.log('[MODULE_LOADER] Waiting for plugins to load::', duration);
            const timeoutId = setTimeout(() => {
                if (noOfPluginsLoaded !== totalPlugins) {
                    window.removeEventListener('alpha:plugins:registered', pluginRegistered);
                    console.error(
                        `[MODULE_LOADER] Timeout reached. Only ${noOfPluginsLoaded} out of ${totalPlugins} has been loaded`,
                    );
                    reject('Timeout reached');
                    alert(
                        `Application failed to load after waiting for ${duration / 1000} seconds. Please reload the page.`,
                    );
                    window.location.reload();
                }
            }, duration);
            let noOfPluginsLoaded = 0;
            let totalPlugins = -1;
            window.environments = this.environment;
            const pluginRegistered = (event: Event) => {
                noOfPluginsLoaded++;
                console.log('[MODULE_LOADER] registration notification:', { event, noOfPluginsLoaded, totalPlugins });
                if (noOfPluginsLoaded === totalPlugins) {
                    clearTimeout(timeoutId);
                    window.removeEventListener('alpha:plugins:registered', pluginRegistered);
                    console.log('[MODULE_LOADER] All plugins loaded');
                    resolve(true);
                } else {
                    console.log(
                        `[MODULE_LOADER] Waiting for all plugins to load: ${noOfPluginsLoaded} out of ${totalPlugins} has been loaded`,
                    );
                }
            };
            window.addEventListener('alpha:plugins:registered', pluginRegistered);
            try {
                const { pluginsLength } = await this.requestForBundles();
                totalPlugins = pluginsLength;
                if (this.loadElements) {
                    await this.requestForElements();
                }
            } catch (e: any) {
                clearTimeout(timeoutId);
                window.removeEventListener('alpha:plugins:registered', pluginRegistered);
                console.error('[MODULE_LOADER] Error loading plugins:', e);
                resolve(e);
            }
        });
    };

    private getModuleServerApiBody(path: string) {
        const apiOptions: any = {
            body: {},
            path: path,
            url: {
                type: 'environment',
                value: {
                    __alpha_env__: 'ALPHA_UI_MODULE_SERVICE_URL',
                },
            },
            headers: {
                'Content-Type': 'application/json',
            },
            method: 'get',
        };
        if (this.environment.SKIP_ALPHA_AUTH !== 'true') {
            apiOptions.apiAuth = {
                authType: 'client',
                ids_url: {
                    type: 'environment',
                    value: {
                        __alpha_env__: 'IDS_URL',
                    },
                },
                path: '/token',
                method: 'post',
                grant_type: {
                    key: 'string',
                    value: 'client_credentials',
                },
                headers: [
                    {
                        key: 'Content-Type',
                        value: 'application/x-www-form-urlencoded',
                    },
                ],
                body: [
                    {
                        key: 'client_secret',
                        type: 'environment',
                        value: {
                            __alpha_env__: 'IDS_CLIENT_SECRET',
                        },
                    },
                    {
                        key: 'client_id',
                        type: 'environment',
                        value: {
                            __alpha_env__: 'IDS_CLIENT_ID',
                        },
                    },
                ],
            };
        }
        return apiOptions;
    }

    private registerToScript(bundle: any) {
        const script = document.createElement('script');
        let bundleUrl = bundle.url;
        if (this.bundlesScriptBasePath) {
            const moduleServicePath = bundleUrl.split('/bundles')[1];
            bundleUrl = `${this.bundlesScriptBasePath}${moduleServicePath}`;
        }
        script.setAttribute('src', bundleUrl);
        script.setAttribute('type', 'module');
        script.setAttribute('integrity', bundle.integrity);
        script.setAttribute('crossorigin', 'use-credentials');

        document.head.append(script);
    }

    private async requestForElements() {
        const script = document.createElement('script');
        try {
            const elementsReqBody = this.getModuleServerApiBody('/elements');

            const alphaElementsUrls = await fetch(
                this.environment.ALPHA_UI_MAKE_REQUEST_URL + '?' + elementsReqBody.path,
                {
                    method: 'post',
                    body: JSON.stringify(elementsReqBody),
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                },
            ).then((res) => {
                if (!res.ok) {
                    if (res.status === 401) {
                        this.router.navigate(['unauthorized'], {
                            queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                        });
                    }
                }
                return res.json();
            });

            const getScriptContent = (elementUrls: string[]) => {
                return `
                    const imports = [${(Array.isArray(elementUrls) ? elementUrls : [])
                        .map((imp: any) => `'${typeof imp === 'object' ? imp.src : imp}'`)
                        .join(',')}];

                    const elementUrlPromise = imports.map((url) => {
                      return import(url);
                    });
                    await Promise.allSettled(elementUrlPromise);
                `;
            };

            script.innerHTML = getScriptContent(alphaElementsUrls);
            script.type = 'module';
            script.crossOrigin = 'use-credentials';
            document.head.appendChild(script);
        } catch (error) {
            console.error('Error fetching Elements:', error);
        }
    }

    private async requestForBundles() {
        const bundlesReqBody = this.getModuleServerApiBody('/bundles');
        const alphaPlugins = await fetch(`${this.environment.ALPHA_UI_MAKE_REQUEST_URL}?${bundlesReqBody.path}`, {
            method: 'post',
            body: JSON.stringify(bundlesReqBody),
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        })
            .then((res) => {
                if (res.ok) {
                    if (res.status === 401) {
                        this.router.navigate(['unauthorized'], {
                            queryParams: { redirectUrl: encodeURIComponent(window.location.href) },
                        });
                    }
                }
                return res.json();
            })
            .catch((e) => Promise.reject(e));
        if (!alphaPlugins?.main || !Array.isArray(alphaPlugins?.plugins)) {
            return Promise.reject('Invalid response from server while fetching plugins');
        }
        if (alphaPlugins?.main) {
            window.addEventListener('alpha_ready', () => {
                alphaPlugins?.plugins.forEach((bundle: { url: string; integrity: string }) => {
                    this.registerToScript(bundle);
                });
            });
            this.registerToScript(alphaPlugins.main);
        }
        return {
            pluginsLength: alphaPlugins.plugins.length,
        };
    }
}
